import is from '@sindresorhus/is';
import { useEffect, useRef } from 'react';

import { resolveAnchor } from '../anchor/resolve';
import { useCommentsScope } from '../comments-scope-context';
import { scrollAnchorIntoViewportIfNeeded } from '../scroll-anchor-into-view';
import { useOptionalCommentsGroupStoreApi } from './comments-group-context';

// An activation that arrives while the comment tile is still closed reflows the whole workspace
// as the tile opens — tiles narrow, rows wrap, the anchor moves. The immediate pass covers the
// already-open case; the later passes re-check once the layout has settled. Each pass no-ops
// when the row is already in view, so re-running is free.
const REVEAL_PASS_DELAYS_MS = [400, 800] as const;

type RevealHandles = {
    readonly frame: number;
    readonly timeouts: readonly number[];
};

/**
 * Brings the anchor row into view inside this member's tile when the group activates one of its
 * threads — a card in the rail can point at a row that is scrolled out of the source tile, and
 * highlighting something invisible reads as nothing happening.
 *
 * Only transitions are acted on, so a member that already owns the activation is not re-scrolled
 * on unrelated store updates, and the user can scroll away from an active thread freely.
 */
export const useScrollActivatedAnchorIntoView = (memberId: string): void => {
    const store = useOptionalCommentsGroupStoreApi();
    const { itemId } = useCommentsScope();
    const scrolledThreadIdRef = useRef<string | null>(null);
    const handlesRef = useRef<RevealHandles | null>(null);

    useEffect(() => {
        if (is.null(store)) return;

        const cancelPasses = (): void => {
            const handles = handlesRef.current;
            if (is.null(handles)) return;
            window.cancelAnimationFrame(handles.frame);
            for (const timeout of handles.timeouts) {
                window.clearTimeout(timeout);
            }
            handlesRef.current = null;
        };

        const apply = (): void => {
            const activation = store.getState().activation;

            if (is.null(activation) || activation.memberId !== memberId) {
                scrolledThreadIdRef.current = null;
                cancelPasses();
                return;
            }
            if (scrolledThreadIdRef.current === activation.threadId) return;
            scrolledThreadIdRef.current = activation.threadId;
            cancelPasses();

            // Re-resolved on every pass: the tile opening can remount the row element.
            const reveal = (): void => {
                const anchorEl = resolveAnchor({
                    itemId,
                    commentId: activation.threadId,
                    target: activation.target,
                });
                if (is.null(anchorEl)) return;
                scrollAnchorIntoViewportIfNeeded(anchorEl, anchorEl.getBoundingClientRect());
            };

            reveal();
            handlesRef.current = {
                frame: window.requestAnimationFrame(reveal),
                timeouts: REVEAL_PASS_DELAYS_MS.map((delay) => window.setTimeout(reveal, delay)),
            };
        };

        apply();

        const unsubscribe = store.subscribe((state, previous) => {
            if (state.activation === previous.activation) return;
            apply();
        });

        return () => {
            unsubscribe();
            cancelPasses();
        };
    }, [itemId, memberId, store]);
};
